import React, {
  useCallback, useContext, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge, Button, Col, Row,
} from 'react-bootstrap';
import { IoIosAddCircleOutline } from 'react-icons/io';
import { CellProps } from 'react-table';
import Table from 'components/Table';
import DateText from 'components/DateText';
import { ModalErrorContext } from 'context/ModalError';
import ProcessService from 'services/api/Process';
import handlePromise from 'utils/Promise';
import { DTable } from 'types/table';
import {
  ProcessDocumentItem,
  PROCESS_OPERATION_TYPE_OPTIONS,
  ProcessOperationType,
  REPARTO_CAPITAL_SUBTYPE_OPTIONS,
} from 'types/Process';
import {
  BsCardText,
  BsCheck,
  BsEnvelope,
  BsFileEarmarkText,
} from 'react-icons/bs';
import CreateProcessModal from '../CreateProcessModal';
import ProcessDetailModal, { ProcessListItem } from '../ProcessDetailModal';
import ProcessDocumentsModal from '../ProcessDocumentsModal';

function StatusBadge( { value }: { value?: string } ) {
  if ( value === 'OK' || value === 'Finalizado' ) {
    return <Badge><BsCheck size={20} /></Badge>;
  }
  if ( value === 'Error' ) return <Badge bg="danger"><BsCheck size={20} /></Badge>;
  if ( value === 'Pendiente' ) {
    return <Badge bg="light"><BsCheck size={20} /></Badge>;
  }
  return value ? <Badge bg="secondary">{value}</Badge> : null;
}

const PROCESS_ID_SUFFIX_LEN = 5;

function formatProcessIdForList( id: string | undefined ): { display: string, title: string } {
  const s = id != null ? String( id ).trim() : '';
  if ( !s ) return { display: '—', title: '' };
  if ( s.length <= PROCESS_ID_SUFFIX_LEN ) return { display: s, title: s };
  return {
    display: s.slice( -PROCESS_ID_SUFFIX_LEN ),
    title: s,
  };
}

function formatOperationTypeWithSubtype(
  process: ProcessListItem,
  operationTypeValue: string | undefined,
  translate: ( key: string ) => string,
): React.ReactNode {
  const option = PROCESS_OPERATION_TYPE_OPTIONS.find( ( o ) => o.value === operationTypeValue );
  const base = option ? translate( option.labelKey ) : operationTypeValue ?? '';
  if (
    process.operationType === ProcessOperationType.REPARTO_CAPITAL
    && process.repartoCapitalSubtype
  ) {
    const sub = REPARTO_CAPITAL_SUBTYPE_OPTIONS.find(
      ( o ) => o.value === process.repartoCapitalSubtype,
    );
    const subText = sub
      ? translate( sub.labelKey )
      : String( process.repartoCapitalSubtype );
    return (
      <>
        {base}
        {' '}
        <span className="text-muted">
          (
          {subText}
          )
        </span>
      </>
    );
  }
  return base;
}

function getComputedGlobalStatus( process: ProcessListItem ): string {
  const stepStatuses = [
    process.step2Status,
    process.step3Status,
    process.step4Status,
    process.step5Status,
  ];
  if ( stepStatuses.some( ( status ) => status === 'Error' ) ) return 'Error';
  if ( stepStatuses.every( ( status ) => status === 'OK' ) ) return 'OK';
  return 'Pendiente';
}

function ProcessesView( {
  data,
  fetchData,
  isLoading,
  reloadData,
}: {
  data: DTable;
  fetchData: ( params: any, pageSize: number ) => void;
  isLoading?: boolean;
  reloadData: () => void;
} ) {
  const { t } = useTranslation();
  const { openErrorModal } = useContext( ModalErrorContext );
  const [showCreateProcessModal, setShowCreateProcessModal] = useState( false );
  const [detailProcess, setDetailProcess] = useState<ProcessListItem | null>( null );
  const [selectedProcessForDocuments, setSelectedProcessForDocuments] = useState<
  ProcessListItem | null
  >( null );
  const [processDocuments, setProcessDocuments] = useState<ProcessDocumentItem[]>( [] );
  const [tableActionsLoading, setTableActionsLoading] = useState( false );

  const closeCreateProcessModal = useCallback( () => {
    setShowCreateProcessModal( false );
  }, [] );

  const openDetailModal = useCallback( ( row: ProcessListItem ) => {
    setDetailProcess( row );
  }, [] );

  const closeDetailModal = useCallback( () => {
    setDetailProcess( null );
  }, [] );

  const getApiErrorMessage = useCallback( ( response: any, fallback: string ) => {
    const key = response?.data?.key;
    if ( key ) {
      const translated = t( `admin.processes.apiErrors.${key}` );
      if ( translated !== `admin.processes.apiErrors.${key}` ) return translated;
    }
    return response?.data?.message || fallback;
  }, [t] );

  const openDocumentsModal = useCallback( async ( row: ProcessListItem ) => {
    if ( !row?.id ) {
      openErrorModal( t( 'admin.processes.processIdMissing' ) );
      return;
    }
    setSelectedProcessForDocuments( row );
    setTableActionsLoading( true );
    setProcessDocuments( [] );
    const [err, response, responseData] = await handlePromise(
      ProcessService.getProcessDocuments( row.id ),
    );
    setTableActionsLoading( false );
    if ( err || !response?.ok ) {
      openErrorModal( getApiErrorMessage( response, t( 'admin.processes.documentsLoadError' ) ) );
      return;
    }
    setProcessDocuments( responseData?.files ?? [] );
  }, [getApiErrorMessage, openErrorModal, t] );

  const closeDocumentsModal = useCallback( () => {
    setSelectedProcessForDocuments( null );
    setProcessDocuments( [] );
  }, [] );

  const downloadDocument = useCallback( async ( fileName: string ) => {
    const processId = selectedProcessForDocuments?.id ? String( selectedProcessForDocuments.id ) : '';
    if ( !processId || !fileName ) {
      openErrorModal( t( 'admin.processes.documentsDownloadError' ) );
      return;
    }
    const [err, response, blobData] = await handlePromise(
      ProcessService.downloadProcessDocument( processId, fileName ),
    );
    if ( err || !response?.ok || !blobData ) {
      openErrorModal( getApiErrorMessage( response, t( 'admin.processes.documentsDownloadError' ) ) );
      return;
    }
    const blobUrl = window.URL.createObjectURL( blobData );
    const link = document.createElement( 'a' );
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild( link );
    link.click();
    document.body.removeChild( link );
    window.URL.revokeObjectURL( blobUrl );
  }, [getApiErrorMessage, openErrorModal, selectedProcessForDocuments?.id, t] );

  const regenerateAndDownloadDocument = useCallback( async ( fileName: string ) => {
    const processId = selectedProcessForDocuments?.id ? String( selectedProcessForDocuments.id ) : '';
    if ( !processId || !fileName ) {
      openErrorModal( t( 'admin.processes.documentsDownloadError' ) );
      return;
    }
    const [err, response, blobData] = await handlePromise(
      ProcessService.downloadProcessDocument( processId, fileName, { regenerate: true } ),
    );
    if ( err || !response?.ok || !blobData ) {
      openErrorModal( getApiErrorMessage( response, t( 'admin.processes.documentsRegenerateError' ) ) );
      return;
    }
    const blobUrl = window.URL.createObjectURL( blobData );
    const link = document.createElement( 'a' );
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild( link );
    link.click();
    document.body.removeChild( link );
    window.URL.revokeObjectURL( blobUrl );
  }, [getApiErrorMessage, openErrorModal, selectedProcessForDocuments?.id, t] );

  const sendNotifications = useCallback( async ( row: ProcessListItem ) => {
    const processId = row?.id ? String( row.id ) : '';
    if ( !processId ) {
      openErrorModal( t( 'admin.processes.processIdMissing' ) );
      return;
    }
    setTableActionsLoading( true );
    const [err, response] = await handlePromise(
      ProcessService.sendProcessNotifications( processId ),
    );
    setTableActionsLoading( false );
    if ( err || !response?.ok ) {
      openErrorModal( getApiErrorMessage( response, t( 'admin.processes.sendNotificationsError' ) ) );
      return;
    }
    reloadData();
  }, [getApiErrorMessage, openErrorModal, reloadData, t] );

  const columns = useMemo( () => ( [
    {
      Header: t( 'admin.processes.shortId' ),
      id: 'shortProcessId',
      accessor: 'id',
      disableSortBy: true,
      Cell: ( { row }: CellProps<any> ) => {
        const process = row.original as ProcessListItem;
        const { display, title } = formatProcessIdForList( process.id );
        return (
          <span className="text-monospace" title={title || undefined}>
            {display}
          </span>
        );
      },
    },
    {
      Header: t( 'admin.processes.createdAt' ),
      accessor: 'createdAt',
      disableSortBy: true,
      Cell: ( { cell }: CellProps<any> ) => ( <DateText value={cell.value} /> ),
    },
    {
      Header: t( 'admin.processes.updatedAt' ),
      accessor: 'updatedAt',
      disableSortBy: true,
      Cell: ( { cell }: CellProps<any> ) => ( <DateText value={cell.value} /> ),
    },
    {
      Header: t( 'admin.processes.operationType' ),
      accessor: 'operationType',
      disableSortBy: true,
      Cell: ( { cell, row }: CellProps<any> ) => formatOperationTypeWithSubtype(
        row.original as ProcessListItem,
        cell.value,
        t,
      ),
    },
    {
      Header: t( 'admin.processes.globalStatus' ),
      accessor: 'globalStatus',
      className: 'text-center',
      headerClassName: 'justify-content-center',
      disableSortBy: true,
      Cell: ( { row }: CellProps<any> ) => (
        <StatusBadge value={getComputedGlobalStatus( row.original as ProcessListItem )} />
      ),
    },
    {
      Header: t( 'admin.processes.notification' ),
      accessor: 'notificationResult',
      disableSortBy: true,
      Cell: ( { cell }: CellProps<any> ) => ( cell.value
        ? t( `admin.processes.notifications.${cell.value}` ) : '-' ),
    },
    {
      Header: t( 'admin.common.actions' ),
      id: 'actions',
      headerClassName: 'justify-content-end pe-2',
      disableSortBy: true,
      Cell: ( { row }: CellProps<any> ) => {
        const process = row.original as ProcessListItem;
        const canSendEmails = process.step2Status === 'OK' && process.step3Status === 'OK';
        const canViewDocuments = process.step3Status === 'OK';
        return (
          <div className="d-flex align-items-center justify-content-end gap-2">
            <Button
              variant="link"
              className="p-0"
              disabled={!canViewDocuments}
              title={canViewDocuments ? undefined : t( 'admin.processes.documentsStep3NotReady' )}
              aria-label={t( 'admin.processes.viewDocuments' )}
              onClick={() => openDocumentsModal( process )}
            >
              <BsFileEarmarkText />
            </Button>
            <Button
              variant="link"
              className="p-0"
              disabled={!canSendEmails}
              onClick={() => sendNotifications( process )}
            >
              <BsEnvelope />
            </Button>
            <Button
              variant="link"
              className="p-0"
              title={t( 'admin.processes.viewDetail' )}
              aria-label={t( 'admin.processes.viewDetail' )}
              onClick={() => openDetailModal( process )}
            >
              <BsCardText className="text-primary" />
            </Button>
          </div>
        );
      },
    },
  ] ), [
    openDetailModal,
    openDocumentsModal,
    sendNotifications,
    t,
  ] );

  return (
    <>
      <Table
        header={() => (
          <Row className="align-items-center">
            <Col>
              <Button
                size="sm"
                className="pt-2 pb-2 ps-4 pe-4 text--normal btn--rounded d-flex align-items-center"
                onClick={() => setShowCreateProcessModal( true )}
              >
                <IoIosAddCircleOutline size={24} className="me-2" />
                {t( 'admin.processes.createProcess' )}
              </Button>
            </Col>
          </Row>
        )}
        columns={columns}
        isLoading={isLoading || tableActionsLoading}
        data={data.data ?? []}
        total={data.totalItems ?? 0}
        pageCount={data.pageCount ?? 0}
        fetchData={fetchData}
      />

      {showCreateProcessModal && (
        <CreateProcessModal
          onClose={closeCreateProcessModal}
          reloadData={reloadData}
        />
      )}

      <ProcessDetailModal
        show={detailProcess != null}
        onClose={closeDetailModal}
        process={detailProcess}
      />

      <ProcessDocumentsModal
        show={selectedProcessForDocuments != null}
        onClose={closeDocumentsModal}
        isLoading={false}
        files={processDocuments}
        onDownload={downloadDocument}
        onRegeneratePdfDownload={regenerateAndDownloadDocument}
      />
    </>
  );
}

export default ProcessesView;
